// @spec GHG-STR-048
// @spec GHG-STR-049
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useMembersSearch } from '@/api/use-members-search';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateReductionInitiative } from '@/features/ghg/api/use-reduction-initiatives';
import {
  ReductionInitiativeForm,
  type ReductionInitiativeFormValues,
} from '@/features/ghg/components/ReductionInitiativeForm';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/reduction-initiatives/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.newReductionInitiative'),
  }),
  component: NewReductionInitiativePage,
});

/**
 * Create new reduction initiative page.
 *
 * Provides a form to configure a new reduction initiative,
 * including category, impacted scopes, estimates, and timeline.
 */
function NewReductionInitiativePage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const createInitiative = useCreateReductionInitiative();
  const { data: membersData, isError: membersError } = useMembersSearch({ limit: 50 });
  const members =
    membersData?.members.map((m) => ({
      id: m.userId,
      name: m.displayName ?? m.email,
    })) ?? [];

  const handleBack = () => {
    void navigate({ to: '/ghg/reduction-initiatives' });
  };

  const handleSubmit = (data: ReductionInitiativeFormValues) => {
    createInitiative.mutate(
      {
        name: data.name,
        description: data.description,
        category: data.category,
        impactedScopes: data.impactedScopes,
        ...(data.impactedActivityGroupIds && { impactedActivityGroupIds: data.impactedActivityGroupIds }),
        estimatedAnnualReduction: data.estimatedAnnualReduction,
        ...(data.estimatedInvestment !== undefined && {
          estimatedInvestment: data.estimatedInvestment,
        }),
        ...(data.estimatedPaybackYears !== undefined && {
          estimatedPaybackYears: data.estimatedPaybackYears,
        }),
        ...(data.plannedStartDate && { plannedStartDate: data.plannedStartDate }),
        ...(data.plannedEndDate && { plannedEndDate: data.plannedEndDate }),
        ownerId: data.ownerId,
      },
      {
        onSuccess: (result) => {
          toast.success(t('toast.reductionInitiativeCreatedSuccessfully'));
          void navigate({
            to: '/ghg/reduction-initiatives/$initiativeId',
            params: { initiativeId: result.id },
          });
        },
        onError: () => {
          toast.error(t('toast.failedToCreateReductionInitiative'));
        },
      }
    );
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('initiativeNew.back')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('initiativeNew.heading')}</h1>
          <p className="text-muted-foreground">
            {t('initiativeNew.description')}
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('initiativeNew.cardTitle')}</CardTitle>
            <CardDescription>
              {t('initiativeNew.cardDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReductionInitiativeForm
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isSubmitting={createInitiative.isPending}
              members={members}
              membersError={membersError}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
